import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Inbox, Menu, TablePropertiesIcon } from 'lucide-react'
import { useProjectStageStore } from '@/components/common/menu/lib/store/useProjectStageStore.tsx'
import { useMenuStore } from '@/store/useMenuStore.ts'
import BottomTabBarItem from '@/components/common/menu/BottomTabBarItem.tsx'
import BottomTabBarIssuesSheet from '@/components/common/menu/BottomTabBarIssuesSheet.tsx'
import BottomTabBarMoreSheet from '@/components/common/menu/BottomTabBarMoreSheet.tsx'

/*
Three-tab bottom navigation. The design intent:

  - Tabs that go to a single destination = direct navigation (Inbox).
  - Tabs where the user has to choose between N siblings = popup (Zgłoszenia,
    which has many issue types under it).
  - Everything else (Dashboard, Reports, Queue, Settings, Logout, switchers)
    lives in the main drawer, opened via "Więcej". No duplicated nav surface;
    "More" literally is the main menu.

Previously this bar had 5 tabs (Summary, Inbox, Zgłoszenia, Reports, More)
with a bespoke "More" sheet — two competing nav surfaces to maintain. This
version collapses it to one: the drawer that already exists.
*/
export default function BottomTabBar() {
  const { t } = useTranslation()
  const stageIri = useProjectStageStore(s => s.iri)
  const unreadNotificationsCount = useProjectStageStore(s => s.unreadNotificationsCount)
  const openMobileMenu = useMenuStore(s => s.openMobileMenu)
  const mobileMenuState = useMenuStore(s => s.mobileMenuState)
  const { pathname } = useLocation()
  const [issuesSheetOpen, setIssuesSheetOpen] = useState(false)

  const notificationsPath = stageIri ? `${stageIri}/notifications` : null

  // Zgłoszenia is active on the archive view and any per-issue-type list.
  const isIssuesActive = /\/issue_types\/|\/archive(\/|$|\?)/.test(pathname)
  const isMoreActive = mobileMenuState !== 'closedMobile'

  return (
    <>
      <nav
        aria-label="Primary mobile navigation"
        className={`fixed inset-x-0 bottom-0 z-[100] flex border-t bg-background pb-[var(--safe-area-inset-bottom)] md:hidden`}
        style={{ height: 'calc(var(--bottomTabBarHeight) + var(--safe-area-inset-bottom))' }}
        data-cy="bottom-tab-bar"
      >
        <BottomTabBarItem
          dataCy="bottom-tab-inbox"
          label={t('navigation.mobile.inbox')}
          icon={<Inbox className={`size-5`} />}
          to={notificationsPath ?? undefined}
          badge={unreadNotificationsCount}
          badgeVariant="dark"
        />
        <BottomTabBarItem
          dataCy="bottom-tab-issues"
          label={t('navigation.mobile.issues')}
          icon={<TablePropertiesIcon className={`size-5`} />}
          onClick={() => setIssuesSheetOpen(true)}
          isActive={isIssuesActive || issuesSheetOpen}
        />
        <BottomTabBarItem
          dataCy="bottom-tab-more"
          label={t('navigation.mobile.more')}
          icon={<Menu className={`size-5`} />}
          // If the user is currently inside /settings/*, open the sheet
          // directly at the secondary layer — they're in "settings mode",
          // so Więcej should show them the settings nav, not the primary
          // one-tap-away-from-settings menu.
          onClick={() =>
            openMobileMenu(pathname.startsWith('/settings') ? 'settings' : 'projectStages')
          }
          isActive={isMoreActive}
        />
      </nav>

      <BottomTabBarIssuesSheet open={issuesSheetOpen} onOpenChange={setIssuesSheetOpen} />
      <BottomTabBarMoreSheet />
    </>
  )
}
